import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import AuthHero from '../../shared/auth/components/AuthHero';
import { companyApiService } from '../services/companyApi';

const InviteTeam: React.FC = () => {
    const navigate = useNavigate();
    const [inviteEmail, setInviteEmail] = useState('');
    const [copySuccess, setCopySuccess] = useState(false);
    const [isLoading, setIsLoading] = useState(false);
    const [successMessage, setSuccessMessage] = useState('');
    const [errorMessage, setErrorMessage] = useState('');
    const [companyId, setCompanyId] = useState<string | null>(null);
    const [sentInvitations, setSentInvitations] = useState<string[]>([]);

    // Get company ID from localStorage on mount
    useEffect(() => {
        // Get company ID from companyData
        const storedCompany = localStorage.getItem('companyData');
        if (storedCompany) {
            try {
                const company = JSON.parse(storedCompany);
                if (company.id) {
                    setCompanyId(company.id);
                    console.log('✅ Company ID loaded from companyData:', company.id);
                    return;
                }
            } catch (err) {
                console.error('Failed to parse company data:', err);
            }
        }

        // Fallback: Check user data for companyId
        const storedUser = localStorage.getItem('authUser');
        if (storedUser) {
            try {
                const user = JSON.parse(storedUser);
                if (user.companyId) {
                    setCompanyId(user.companyId);
                    console.log('✅ Company ID loaded from authUser:', user.companyId);
                    return;
                }
            } catch (err) {
                console.error('Failed to parse user data:', err);
            }
        }

        // If no company ID found, show error
        console.error('❌ Company ID not found in localStorage');
        setErrorMessage('Company ID not found. Please complete company profile setup first.');
    }, []);

    // Dynamic invite link - generated after sending an invitation
    const [inviteLink, setInviteLink] = useState<string>('');

    const handleCopyLink = async () => {
        if (!inviteLink) return;
        try {
            await navigator.clipboard.writeText(inviteLink);
            setCopySuccess(true);
            setTimeout(() => setCopySuccess(false), 2000);
        } catch (err) {
            console.error('Failed to copy:', err);
        }
    };

    const handleSendEmail = async () => {
        if (!inviteEmail.trim()) return;

        // Validate email format
        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        if (!emailRegex.test(inviteEmail)) {
            setErrorMessage('Please enter a valid email address');
            setTimeout(() => setErrorMessage(''), 3000);
            return;
        }

        // Check if we have company ID
        if (!companyId) {
            setErrorMessage('Company ID not found. Please complete company profile setup first.');
            setTimeout(() => setErrorMessage(''), 5000);
            return;
        }

        setIsLoading(true);
        setErrorMessage('');
        setSuccessMessage('');

        try {
            // Call Go backend API via companyApiService
            const result = await companyApiService.inviteRecruiter(companyId, inviteEmail.trim());

            // Build the real invite link from the response
            if (result.invitationId) {
                const joinUrl = `${window.location.origin}/auth/recruiter/join?invitation_id=${result.invitationId}&email=${encodeURIComponent(inviteEmail.trim())}`;
                setInviteLink(joinUrl);
            }

            // Show success message
            setSuccessMessage(`Invitation sent to ${inviteEmail}`);

            // Add to sent invitations list
            setSentInvitations(prev => [...prev, inviteEmail]);

            // Clear input
            setInviteEmail('');

            // Clear success message after 3 seconds
            setTimeout(() => setSuccessMessage(''), 3000);

            console.log('Invitation sent successfully. Check Supabase company_memberships table for invitation_id:', result);

        } catch (err: any) {
            console.error('Failed to send invitation:', err);

            // Handle specific error cases
            if (err.message.includes('already exists') || err.message.includes('already a member')) {
                setErrorMessage('This email is already invited or is a member');
            } else if (err.message.includes('Authentication required')) {
                setErrorMessage('Please log in again to send invitations');
            } else {
                setErrorMessage(err.message || 'Failed to send invitation. Please try again.');
            }

            // Clear error message after 5 seconds
            setTimeout(() => setErrorMessage(''), 5000);
        } finally {
            setIsLoading(false);
        }
    };

    const handleSkip = () => {
        // Navigate to company dashboard
        navigate('/companies/dashboard');
    };

    const handleSendInvites = () => {
        // Navigate to dashboard after sending invites
        navigate('/companies/dashboard');
    };

    return (
        <div className="min-h-screen bg-[#F8F9FB] flex">
            {/* Left Side - Invite Management */}
            <div className="flex-1 flex items-center justify-center p-8">
                <div className="w-full max-w-[634px]">
                    {/* Logo */}
                    <div className="mb-8">
                        <img
                            src="/branding/logo_blue.svg"
                            alt="Hush"
                            className="h-12 w-auto"
                        />
                    </div>

                    {/* Title */}
                    <h1
                        style={{
                            fontFamily: 'Manrope',
                            fontWeight: 700,
                            fontSize: '48px',
                            lineHeight: '100%',
                            color: '#015FA6',
                            marginBottom: '8px'
                        }}
                    >
                        Invite Your Recruiting Team
                    </h1>

                    {/* Subtitle */}
                    <p
                        style={{
                            fontFamily: 'SF Pro',
                            fontWeight: 110,
                            fontSize: '18px',
                            lineHeight: '150%',
                            color: '#015FA6',
                            marginBottom: '48px'
                        }}
                    >
                        Collaborate on candidate searches, share notes, and manage roles together
                    </p>

                    {/* Success/Error Messages */}
                    {successMessage && (
                        <div
                            style={{
                                padding: '12px 16px',
                                background: '#10B981',
                                color: '#FFFFFF',
                                borderRadius: '8px',
                                marginBottom: '24px',
                                fontFamily: 'Manrope',
                                fontSize: '14px'
                            }}
                        >
                            {successMessage}
                        </div>
                    )}

                    {errorMessage && (
                        <div
                            style={{
                                padding: '12px 16px',
                                background: '#EF4444',
                                color: '#FFFFFF',
                                borderRadius: '8px',
                                marginBottom: '24px',
                                fontFamily: 'Manrope',
                                fontSize: '14px'
                            }}
                        >
                            {errorMessage}
                        </div>
                    )}

                    {/* Sent Invitations List */}
                    {sentInvitations.length > 0 && (
                        <div
                            style={{
                                padding: '16px',
                                background: '#E0F2FE',
                                borderRadius: '8px',
                                marginBottom: '24px'
                            }}
                        >
                            <h4
                                style={{
                                    fontFamily: 'Manrope',
                                    fontWeight: 600,
                                    fontSize: '16px',
                                    color: '#015FA6',
                                    marginBottom: '8px'
                                }}
                            >
                                Invitations Sent ({sentInvitations.length})
                            </h4>
                            <ul style={{ listStyle: 'none', padding: 0, margin: 0 }}>
                                {sentInvitations.map((email, index) => (
                                    <li
                                        key={index}
                                        style={{
                                            fontFamily: 'Manrope',
                                            fontSize: '14px',
                                            color: '#015FA6',
                                            padding: '4px 0'
                                        }}
                                    >
                                        • {email}
                                    </li>
                                ))}
                            </ul>
                        </div>
                    )}

                    {/* Copy Invite Link Card */}
                    <div
                        style={{
                            width: '100%',
                            background: '#FFFFFF',
                            boxShadow: '0px 2px 15px rgba(0, 0, 0, 0.1)',
                            borderRadius: '16px',
                            padding: '24px',
                            marginBottom: '26px'
                        }}
                    >
                        <h3
                            style={{
                                fontFamily: 'Manrope',
                                fontWeight: 600,
                                fontSize: '24px',
                                lineHeight: '33px',
                                color: '#015FA6',
                                marginBottom: '20px'
                            }}
                        >
                            Copy Invite Link
                        </h3>
                        {inviteLink ? (
                            <div style={{ position: 'relative' }}>
                                <p
                                    style={{
                                        fontFamily: 'Manrope',
                                        fontWeight: 300,
                                        fontSize: '18px',
                                        lineHeight: '150%',
                                        color: '#015FA6',
                                        wordBreak: 'break-all',
                                        paddingRight: '60px'
                                    }}
                                >
                                    {inviteLink}
                                </p>
                                <button
                                    onClick={handleCopyLink}
                                    style={{
                                        position: 'absolute',
                                        right: '0',
                                        top: '50%',
                                        transform: 'translateY(-50%)',
                                        width: '50px',
                                        height: '50px',
                                        border: '0.2px solid #A7A7A7',
                                        borderRadius: '50%',
                                        background: '#FFFFFF',
                                        cursor: 'pointer',
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        transition: 'background-color 0.2s'
                                    }}
                                    onMouseEnter={(e) => e.currentTarget.style.background = '#F7FAFC'}
                                    onMouseLeave={(e) => e.currentTarget.style.background = '#FFFFFF'}
                                >
                                    {copySuccess ? (
                                        <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
                                            <path d="M5 13l4 4L19 7" stroke="#10B981" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                                        </svg>
                                    ) : (
                                        <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
                                            <path d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z" stroke="#015FA6" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                                        </svg>
                                    )}
                                </button>
                            </div>
                        ) : (
                            <p
                                style={{
                                    fontFamily: 'Manrope',
                                    fontWeight: 300,
                                    fontSize: '16px',
                                    lineHeight: '150%',
                                    color: '#7C8493'
                                }}
                            >
                                Send an email invitation below to generate a shareable invite link.
                            </p>
                        )}
                    </div>

                    {/* Send Email Invites Card */}
                    <div
                        style={{
                            width: '100%',
                            background: '#FFFFFF',
                            boxShadow: '0px 2px 15px rgba(0, 0, 0, 0.1)',
                            borderRadius: '16px',
                            padding: '24px',
                            marginBottom: '48px'
                        }}
                    >
                        <h3
                            style={{
                                fontFamily: 'Manrope',
                                fontWeight: 600,
                                fontSize: '24px',
                                lineHeight: '33px',
                                color: '#015FA6',
                                marginBottom: '20px'
                            }}
                        >
                            Send Email Invites
                        </h3>
                        <div style={{ position: 'relative' }}>
                            <input
                                type="email"
                                value={inviteEmail}
                                onChange={(e) => setInviteEmail(e.target.value)}
                                placeholder="example@gmail.com"
                                disabled={isLoading || !companyId}
                                onKeyPress={(e) => {
                                    if (e.key === 'Enter' && !isLoading) {
                                        handleSendEmail();
                                    }
                                }}
                                style={{
                                    width: '100%',
                                    height: '55px',
                                    padding: '10px 50px 10px 12px',
                                    fontFamily: 'Manrope',
                                    fontSize: '16px',
                                    color: '#015FA6',
                                    background: isLoading || !companyId ? '#F1F1F1' : '#F7FAFC',
                                    border: '1px solid #CBD5E0',
                                    borderRadius: '8px',
                                    outline: 'none',
                                    boxShadow: 'inset 0px 2px 0px rgba(231, 235, 238, 0.2)',
                                    letterSpacing: '-0.154px',
                                    cursor: isLoading || !companyId ? 'not-allowed' : 'text'
                                }}
                                onFocus={(e) => {
                                    if (!isLoading && companyId) {
                                        e.target.style.borderColor = '#015FA6';
                                    }
                                }}
                                onBlur={(e) => {
                                    e.target.style.borderColor = '#CBD5E0';
                                }}
                            />
                            <button
                                onClick={handleSendEmail}
                                disabled={!inviteEmail.trim() || isLoading || !companyId}
                                style={{
                                    position: 'absolute',
                                    right: '16px',
                                    top: '50%',
                                    transform: 'translateY(-50%)',
                                    background: 'none',
                                    border: 'none',
                                    cursor: (inviteEmail.trim() && !isLoading && companyId) ? 'pointer' : 'not-allowed',
                                    padding: '4px',
                                    display: 'flex',
                                    alignItems: 'center',
                                    opacity: (inviteEmail.trim() && !isLoading && companyId) ? 1 : 0.4
                                }}
                            >
                                {isLoading ? (
                                    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" style={{ animation: 'spin 1s linear infinite' }}>
                                        <circle cx="12" cy="12" r="10" stroke="#015FA6" strokeWidth="2" strokeDasharray="31.4 31.4" strokeLinecap="round" />
                                    </svg>
                                ) : (
                                    <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
                                        <path d="M2.01 21L23 12 2.01 3 2 10l15 2-15 2z" fill="#015FA6"/>
                                    </svg>
                                )}
                            </button>
                        </div>
                    </div>

                    {/* Action Buttons */}
                    <div style={{ display: 'flex', gap: '24px' }}>
                        <button
                            onClick={handleSkip}
                            disabled={isLoading}
                            style={{
                                flex: 1,
                                height: '55px',
                                fontFamily: 'Manrope',
                                fontWeight: 600,
                                fontSize: '20px',
                                lineHeight: '28px',
                                color: '#015FA6',
                                background: '#FFFFFF',
                                border: '1px solid #015FA6',
                                borderRadius: '8px',
                                cursor: isLoading ? 'not-allowed' : 'pointer',
                                transition: 'all 0.2s ease',
                                opacity: isLoading ? 0.6 : 1
                            }}
                            onMouseEnter={(e) => {
                                if (!isLoading) {
                                    e.currentTarget.style.background = '#F7FAFC';
                                }
                            }}
                            onMouseLeave={(e) => {
                                if (!isLoading) {
                                    e.currentTarget.style.background = '#FFFFFF';
                                }
                            }}
                        >
                            Skip for Now
                        </button>

                        <button
                            onClick={handleSendInvites}
                            disabled={isLoading}
                            style={{
                                flex: 1,
                                height: '55px',
                                fontFamily: 'Manrope',
                                fontWeight: 600,
                                fontSize: '20px',
                                lineHeight: '28px',
                                color: '#F7FAFC',
                                background: isLoading ? '#7CA9CC' : '#015FA6',
                                border: 'none',
                                borderRadius: '8px',
                                cursor: isLoading ? 'not-allowed' : 'pointer',
                                transition: 'background-color 0.2s ease'
                            }}
                            onMouseEnter={(e) => {
                                if (!isLoading) {
                                    e.currentTarget.style.background = '#014580';
                                }
                            }}
                            onMouseLeave={(e) => {
                                if (!isLoading) {
                                    e.currentTarget.style.background = '#015FA6';
                                }
                            }}
                        >
                            {isLoading ? 'Sending...' : 'Send Invites'}
                        </button>
                    </div>
                </div>
            </div>

            {/* Right Side - Hero */}
            <AuthHero variant="company" />

            {/* Add spinning animation for loading */}
            <style>{`
                @keyframes spin {
                    0% { transform: rotate(0deg); }
                    100% { transform: rotate(360deg); }
                }
            `}</style>
        </div>
    );
};

export default InviteTeam;
